
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import TenantSelector from './TenantSelector';
import { dashboardAnalyticsAPI } from '../services/garageApi';
import InOutFlowChart from './dashboard/InOutFlowChart';
import StockDistributionChart from './dashboard/StockDistributionChart';
import StockAgingChart from './common/StockAgingChart';
import ProfitMarginChart from './dashboard/ProfitMarginChart';
import SecurityAlerts from './dashboard/SecurityAlerts';
import RecentMovements from './dashboard/RecentMovements';
import { Link } from 'react-router-dom';
import {
  Users,
  Package,
  ShoppingCart,
  FileText,
  TrendingUp,
  PieChart as PieChartIcon,
  Clock,
  DollarSign,
  AlertTriangle,
  Activity,
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = useTenant();
  const [activeTenant, setActiveTenant] = useState(null);

  const [flowData, setFlowData] = useState(null);
  const [distributionData, setDistributionData] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [marginsData, setMarginsData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [recentMovements, setRecentMovements] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Check if super admin has active tenant context
  useEffect(() => {
    if (isSuperAdmin) {
      const activeTenantId = localStorage.getItem('activeTenantId');
      setActiveTenant(activeTenantId);
    }
  }, [isSuperAdmin]);

  // Fetch analytics data when tenant context is available
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setAnalyticsLoading(true);
        const [flow, dist, aging, margins, alerts, recent] = await Promise.all([
          dashboardAnalyticsAPI.getFlow().catch(() => null),
          dashboardAnalyticsAPI.getDistribution().catch(() => null),
          dashboardAnalyticsAPI.getAging().catch(() => null),
          dashboardAnalyticsAPI.getMargins().catch(() => null),
          dashboardAnalyticsAPI.getAlerts().catch(() => null),
          dashboardAnalyticsAPI.getRecentMovements().catch(() => null),
        ]);
        setFlowData(flow);
        setDistributionData(dist);
        setAgingData(aging);
        setMarginsData(margins);
        setAlertsData(alerts);
        setRecentMovements(recent);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    if (!isSuperAdmin || activeTenant) {
      fetchAnalytics();
    } else {
      setAnalyticsLoading(false);
    }
  }, [isSuperAdmin, activeTenant]);

  // Show tenant selector for super admin if no tenant is selected
  if (user?.super_admin && !activeTenant) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Select a tenant to view data
          </h2>
          <TenantSelector onTenantSwitch={setActiveTenant} />
        </div>
      </div>
    );
  }

  // Regular dashboard content
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome back, {user?.name || 'Admin'}!
          {isSuperAdmin && activeTenant && (
            <span className="ml-2 text-blue-600">
              Viewing data for tenant: {activeTenant}
            </span>
          )}
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link
          to="/customers"
          className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Customers</h3>
              <p className="text-gray-600">Manage customer data</p>
            </div>
          </div>
        </Link>

        <Link
          to="/suppliers"
          className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center">
            <Package className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Suppliers</h3>
              <p className="text-gray-600">Manage supplier data</p>
            </div>
          </div>
        </Link>

        <Link
          to="/inventories"
          className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center">
            <ShoppingCart className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Inventory</h3>
              <p className="text-gray-600">Manage stock levels</p>
            </div>
          </div>
        </Link>

        <Link
          to="/invoices"
          className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
              <p className="text-gray-600">Manage invoices</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Analytics Section */}
      {analyticsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          <span className="ml-3 text-gray-500 text-sm">Loading analytics...</span>
        </div>
      ) : (
        <>
          {/* Row 1: Flow Chart + Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Stock In vs Out (Last 30 Days)
              </h3>
              <InOutFlowChart data={flowData} />
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-purple-600" />
                Stock Distribution
              </h3>
              <StockDistributionChart data={distributionData} />
            </div>
          </div>

          {/* Row 2: Aging + Margins */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                Stock Aging
              </h3>
              <StockAgingChart buckets={agingData} />
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Profit Margins by Category
              </h3>
              <ProfitMarginChart data={marginsData} />
            </div>
          </div>

          {/* Row 3: Security Alerts */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Security Audit &mdash; Suspicious Adjustments
            </h3>
            <SecurityAlerts alerts={alertsData} />
          </div>

          {/* Row 4: Recent Movements */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Recent Inventory Movements
            </h3>
            <RecentMovements movements={recentMovements} />
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
